import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  Suspense,
  useCallback
} from 'react';
import * as T from './types';
import * as S from './styles';
import * as DCCService from '../../services/dcc-service';
import { ProgressIndicator } from '@veneer/core';
import { useTranslation } from 'react-i18next';
import { AnalyticsService } from '@mssi/analytics-ui-lib';

const ManageWidgetModal = React.lazy(
  () => import('src/components/ManageWidgetModal')
);
const DeviceFamily = React.lazy(() => import('src/components/DeviceFamily'));
const DeviceStatus = React.lazy(() => import('src/components/DeviceStatus'));
// const DeviceTypes = React.lazy(() => import('src/components/DeviceTypes'));
const ActiveUsers = React.lazy(() => import('src/components/ActiveUsers'));
const CollectMethod = React.lazy(() => import('src/components/CollectMethod'));
const ServiceRequest = React.lazy(
  () => import('src/components/ServiceRequest')
);
const EndOfTerm = React.lazy(() => import('src/components/EndOfTerm'));
// const TopDevices = React.lazy(() => import('src/components/TopDevices'));
const SupplyOrder = React.lazy(() => import('src/components/SupplyOrder'));
const GeoRegion = React.lazy(() => import('src/components/GeoRegion'));
const NRDcomponent = React.lazy(() => import('src/components/NRDcomponent'));
const Countries = React.lazy(() => import('src/components/Countries'));
const RoomsByModels = React.lazy(() => import('src/components/RoomsByModels'));
const BillingModel = React.lazy(() => import('src/components/BillingModel'));

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const callback = () => setWindowSize(window.innerWidth);

  useEffect(() => {
    window.addEventListener('resize', callback);
    return () => window.removeEventListener('resize', callback);
  }, []);

  return windowSize;
};

const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const PrimaryWidgets = ({
  navigation,
  customerContext,
  isMCSDashboard,
  isMDSDashboard,
  revealHeader = false
}: {
  navigation: any;
  customerContext: any;
  isMCSDashboard: boolean;
  isMDSDashboard: boolean;
  revealHeader?: boolean;
}) => {
  const analytics = AnalyticsService.getInstance();
  const { t } = useTranslation() as any;

  const defaultWidgetArray = useMemo(() => {
    if (isMDSDashboard) {
      return [
        {
          id: 'deviceFamilies',
          key: 'dashboard.customerDashboard.manageWidgets.deviceFamilies',
          name: 'Device Families',
          position: 1,
          show: true
        },
        {
          id: 'deviceByGeoRegion',
          key: 'dashboard.customerDashboard.deviceByGeoRegion.label',
          name: 'Device by GEO Region',
          position: 2,
          show: true
        },
        {
          id: 'countries',
          key: 'dashboard.customerDashboard.countries.countries',
          name: 'Countries',
          position: 3,
          show: true
        },
        {
          id: 'breakFix',
          key: 'dashboard.customerDashboard.manageWidgets.openServiceRequest',
          name: 'Open Service Request',
          position: 4,
          show: true
        },
        {
          id: 'activeUsers',
          key: 'dashboard.customerDashboard.manageWidgets.activeUsers',
          name: 'Active Users',
          position: 5,
          show: true
        }
      ];
    } else if (isMCSDashboard) {
      return [
        {
          id: 'roomsByModels',
          key: 'dashboard.customerDashboard.manageWidgets.roomsByModels',
          name: 'Rooms by Models',
          position: 1,
          show: true
        },
        {
          id: 'deviceByGeoRegion',
          key: 'dashboard.customerDashboard.manageWidgets.deviceByGeoRegion',
          name: 'Device by GEO Region',
          position: 2,
          show: true
        },
        {
          id: 'countries',
          key: 'dashboard.customerDashboard.manageWidgets.countries',
          name: 'Countries',
          position: 3,
          show: true
        },
        {
          id: 'breakFix',
          key: 'dashboard.customerDashboard.manageWidgets.openServiceRequest',
          name: 'Open Service Requests',
          position: 4,
          show: true
        },
        {
          id: 'activeUsers',
          key: 'dashboard.customerDashboard.manageWidgets.activeUsers',
          name: 'Active Users',
          position: 5,
          show: true
        }
      ];
    } else {
      return [
        {
          id: 'deviceFamilies',
          key: 'dashboard.customerDashboard.manageWidgets.deviceFamilies',
          name: 'Device Families',
          position: 1,
          show: true
        },
        {
          id: 'nonReportingDevices',
          key: 'dashboard.customerDashboard.manageWidgets.nonReportingDevices',
          name: 'NRD % of Fleet',
          position: 2,
          show: true
        },
        {
          id: 'deviceStatus',
          key: 'dashboard.customerDashboard.manageWidgets.deviceStatus',
          name: 'Device Status',
          position: 7,
          show: false
        },
        {
          id: 'serviceRequest',
          key: 'dashboard.customerDashboard.manageWidgets.openServiceRequest',
          name: 'Open Service Request',
          position: 3,
          show: true
        },
        {
          id: 'endOfTerm',
          key: 'dashboard.customerDashboard.manageWidgets.endOfTerm',
          name: 'End of Term',
          position: 4,
          show: true
        },
        {
          id: 'supplyOrder',
          key: 'dashboard.customerDashboard.manageWidgets.supplyOrder',
          name: 'Supply Orders',
          position: 9,
          show: false
        },
        {
          id: 'deviceByGeoRegion',
          key: 'dashboard.customerDashboard.manageWidgets.deviceByGeoRegion',
          name: 'Device by GEO Region',
          position: 10,
          show: false
        },
        {
          id: 'collectMethod',
          key: 'dashboard.customerDashboard.manageWidgets.collectMethod',
          name: 'Collect Method',
          position: 11,
          show: true
        },
        {
          id: 'countries',
          key: 'dashboard.customerDashboard.manageWidgets.countries',
          name: 'Countries',
          position: 12,
          show: false
        },
        {
          id: 'billingModel',
          key: 'dashboard.customerDashboard.header.billingModels',
          name: 'Billing Models',
          position: 13,
          show: true
        }
      ];
    }
  }, [isMCSDashboard, isMDSDashboard]);

  const [widgetArray, setWidgetArray] = useState<T.Widget[]>(
    () => defaultWidgetArray
  );

  const resolution = useWindowSize();
  const isWideResolution = resolution > 1750;

  const componentsObject: Record<
    string,
    {
      component: React.LazyExoticComponent<React.FC<any>>;
      props: any;
      className: string;
    }
  > = useMemo(() => {
    if (isMDSDashboard) {
      return {
        deviceFamilies: {
          component: DeviceFamily,
          props: { type: 'laptop', customerContext, isMDSDashboard },
          className: 'squareCardLarge'
        },
        activeUsers: {
          component: ActiveUsers,
          props: { customerContext, isMDSDashboard },
          className: 'squareCardSmall'
        },
        breakFix: {
          component: ServiceRequest,
          props: {
            isWideResolution,
            widgetType: 'breakFix',
            customerContext,
            isMDSDashboard
          },
          className: 'rectangleCardLarge'
        },
        deviceByGeoRegion: {
          component: GeoRegion,
          props: {
            isWideResolution,
            type: 'laptop',
            customerContext,
            customClass: ['squareCardLarge'],
            isMDSDashboard
          },
          className: 'squareCardLarge'
        },
        countries: {
          component: Countries,
          props: { type: 'laptop', customerContext, isMDSDashboard },
          className: 'squareCardLarge'
        }
      };
    } else if (isMCSDashboard) {
      return {
        roomsByModels: {
          component: RoomsByModels,
          props: {
            type: 'Collaboration Room',
            customerContext,
            isMCSDashboard
          },
          className: 'squareCardLarge'
        },
        activeUsers: {
          component: ActiveUsers,
          props: { customerContext, isMCSDashboard },
          className: 'squareCardSmall'
        },
        breakFix: {
          component: ServiceRequest,
          props: {
            isWideResolution,
            widgetType: 'breakFix',
            customerContext,
            isMCSDashboard
          },
          className: 'rectangleCardLarge'
        },
        deviceByGeoRegion: {
          component: GeoRegion,
          props: {
            isWideResolution,
            type: 'Collaboration Room',
            customerContext,
            customClass: ['squareCardLarge'],
            isMCSDashboard
          },
          className: 'squareCardLarge'
        },
        countries: {
          component: Countries,
          props: {
            type: 'Collaboration Room',
            customerContext,
            isMCSDashboard
          },
          className: 'squareCardLarge'
        }
      };
    } else {
      return {
        deviceFamilies: {
          component: DeviceFamily,
          props: { type: 'device', customerContext },
          className: 'squareCardLarge'
        },
        nonReportingDevices: {
          component: NRDcomponent,
          props: { isWideResolution, type: 'device', customerContext },
          className: 'rectangleCardLarge'
        },
        // deviceTypes: {
        //   component: DeviceTypes,
        //   props: { type: 'device' },
        //   className: 'rectangleCardSmall'
        // },
        deviceStatus: {
          component: DeviceStatus,
          props: { type: 'device', customerContext },
          className: 'rectangleCardSmall'
        },
        // activeUsers: {
        //   component: ActiveUsers,
        //   props: { customerContext },
        //   className: 'squareCardSmall'
        // },
        collectMethod: {
          component: CollectMethod,
          props: { isWideResolution, type: 'device', customerContext },
          className: 'rectangleCardSmall'
        },
        serviceRequest: {
          component: ServiceRequest,
          props: { isWideResolution, type: 'device', customerContext },
          className: 'rectangleCardLarge'
        },
        endOfTerm: {
          component: EndOfTerm,
          props: { type: 'device', customerContext },
          className: 'squareCardLarge'
        },
        // top10Devices: {
        //   component: TopDevices,
        //   props: { isWideResolution, type: 'device', customerContext },
        //   className: 'squareCardLarge'
        // },
        supplyOrder: {
          component: SupplyOrder,
          props: { type: 'device', customerContext },
          className: 'rectangleCardLarge'
        },
        deviceByGeoRegion: {
          component: GeoRegion,
          props: {
            isWideResolution,
            type: 'device',
            customerContext,
            className:
              resolution <= 1440 ? 'rectangleCardLarge' : 'rectangleCardSmall'
          },
          className:
            resolution <= 1440 ? 'rectangleCardLarge' : 'rectangleCardSmall'
        },
        countries: {
          component: Countries,
          props: { type: 'device', customerContext },
          className: 'squareCardLarge'
        },
        billingModel: {
          component: BillingModel,
          props: { type: 'billingModel', customerContext },
          className: 'squareCardLarge'
        }
      };
    }
  }, [
    isWideResolution,
    customerContext,
    resolution,
    isMCSDashboard,
    isMDSDashboard
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the user preferences from the service
        const userContext = JSON.parse(localStorage.getItem('userContext')); // Directly use the response from the service
        let dashboardKey = 'customer';
        let localStorageKey = 'widgetArray';

        if (isMDSDashboard) {
          dashboardKey = 'MDScustomer';
          localStorageKey = 'MDSCustomerWidgetArray';
        } else if (isMCSDashboard) {
          dashboardKey = 'MCScustomer';
          localStorageKey = 'MCSCustomerWidgetArray';
        }

        const widgetPreference = userContext?.widgetPreference?.[dashboardKey];

        const billingModelWidget = {
          id: 'billingModel',
          name: 'Billing Models',
          position: 13,
          show: true
        };

        interface WidgetPreference {
          id: string;
        }

        const exists: boolean = (widgetPreference as WidgetPreference[])?.some(
          (widget: WidgetPreference) => widget.id === billingModelWidget.id
        );

        // Add if not exists
        if (!exists && dashboardKey === 'customer') {
          widgetPreference.push(billingModelWidget);
        }

        // Determine the widget array based on user context or localStorage
        let widgetArray =
          widgetPreference ||
          JSON.parse(
            localStorage.getItem(localStorageKey) ||
              JSON.stringify(defaultWidgetArray)
          );

        // Update localStorage and state if necessary
        if (!widgetPreference) {
          localStorage.setItem(localStorageKey, JSON.stringify(widgetArray));
        }

        // Set the widget array state
        if (!isMCSDashboard && !isMDSDashboard) {
          widgetArray = widgetArray
            .filter(
              (widget: T.Widget) =>
                widget.id !== 'deviceTypes' &&
                widget.id !== 'top10Devices' &&
                widget.id !== 'activeUsers'
            )
            .map((widget: T.Widget) => ({
              ...widget,
              key:
                defaultWidgetArray.find((w) => w.id === widget.id)?.key ||
                widget.id
            }));
        }
        setWidgetArray(widgetArray);
      } catch (error) {
        console.error('Error fetching user preference:', error);
      }
      analytics.logEvent('primary_widgets_view', {
        event_label: 'Primary Widgets View',
        action: 'View'
      });
    };

    // Call the fetchData function on component mount
    fetchData();
    // Ensure breadcrumb data is fetched after setting the widget array
    getCustomerBreadcrumbData();
  }, [isMCSDashboard, isMDSDashboard, defaultWidgetArray]); // Add dependencies

  const [breadCrumbData, setBreadCrumbData] = useState<T.BreadCrumb[]>([]);
  const [breadcrumbLoder, setBreadcrumbLoader] = useState<boolean>(true);
  const getCustomerBreadcrumbData = async () => {
    try {
      const response = await DCCService.getBreadcrumbAggregates();
      setBreadCrumbData(response);
      setBreadcrumbLoader(false);
      analytics.logEvent('breadcrumb_data_view', {
        event_label: 'Breadcrumb Data View',
        action: 'View'
      });
    } catch (error) {
      setBreadcrumbLoader(false);
      console.log(error);
      analytics.logEvent('breadcrumb_data_error', {
        event_label: 'Breadcrumb Data Error',
        action: 'View',
        action_result: 'Failure'
      });
    }
  };

  const updateWidgetArray = useCallback(
    debounce((newArray: T.Widget[]) => {
      setWidgetArray(newArray);
      const userContext: any = JSON.parse(localStorage.getItem('userContext'));
      if (isMDSDashboard) {
        // DCCService.updateUserWidgetPreference(newArray, 'MDScustomer');
      } else if (isMCSDashboard) {
        DCCService.updateUserWidgetPreference(newArray, 'MCScustomer');
        if (userContext && userContext.widgetPreference) {
          userContext.widgetPreference.MCScustomer = newArray;
        }
        analytics.logEvent('mcs_widget_preference_update', {
          event_label: 'MCS Widget Preference Update',
          action: 'Edit'
        });
      } else {
        DCCService.updateUserWidgetPreference(newArray, 'customer');
        if (userContext && userContext.widgetPreference) {
          userContext.widgetPreference.customer = newArray;
        }
        analytics.logEvent('widget_preference_update', {
          event_label: 'Widget Preference Update',
          action: 'Edit'
        });
      }
      localStorage.setItem('userContext', JSON.stringify(userContext));
    }, 300),
    [isMCSDashboard, isMDSDashboard]
  );

  useEffect(() => {
    function sortable(
      section: HTMLElement,
      onUpdate: (item: HTMLElement, newIndex: number) => void
    ) {
      let dragEl: HTMLElement | null, nextEl: HTMLElement | null;

      function _onDragOver(e: DragEvent) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const eventTarget = e.target as HTMLElement;
        let target: HTMLElement | null = eventTarget;

        const targetClasses = [
          'squareCardLarge',
          'squareCardSmall',
          'rectangleCardLarge',
          'rectangleCardSmall',
          'squareCardLargeHalf'
        ];

        const hasTargetClass = (element: HTMLElement | null) =>
          element &&
          targetClasses.some((className) =>
            element.classList.contains(className)
          );

        while (target && !hasTargetClass(target)) {
          target = target.parentElement;
        }

        if (target && target !== dragEl) {
          const targetPos = target.getBoundingClientRect();
          const next =
            (e.clientY - targetPos.top) / (targetPos.bottom - targetPos.top) >
            0.5;

          const insertBeforeNode = next ? target.nextSibling : target;

          if (insertBeforeNode && section.contains(insertBeforeNode)) {
            section.insertBefore(dragEl, insertBeforeNode);
          }
        }
      }

      function _onDragEnd(evt: DragEvent) {
        evt.preventDefault();
        if (dragEl) {
          dragEl.classList.remove('ghost');
          analytics.logEvent('widget_reorder_complete', {
            event_label: `Widget Reorder Complete - ${dragEl.id}`,
            action: 'Edit'
          });
        }
        section.removeEventListener('dragover', _onDragOver);
        section.removeEventListener('dragend', _onDragEnd);
        if (nextEl !== dragEl?.nextSibling) {
          const newIndex = Array.from(section.children).indexOf(dragEl!);
          onUpdate(dragEl!, newIndex);
        }
      }

      section.addEventListener('dragstart', function (e) {
        dragEl = e.target as HTMLElement;
        nextEl = dragEl.nextSibling as HTMLElement | null;

        e.dataTransfer.effectAllowed = 'move';
        section.addEventListener('dragover', _onDragOver);
        section.addEventListener('dragend', _onDragEnd);

        analytics.logEvent('widget_reorder_start', {
          event_label: `Widget Reorder Start - ${dragEl.id}`,
          action: 'Edit'
        });

        setTimeout(() => dragEl?.classList.add('ghost'), 0);
      });

      Array.from(section.children).forEach((item) => {
        (item as HTMLElement).draggable = true;
      });
    }

    const listElement = document.getElementById('customerWidgets');
    if (listElement) {
      sortable(listElement, (item: HTMLElement, newIndex: number) => {
        const elements = document.querySelectorAll('#customerWidgets > div');
        const tempWidgets = [...widgetArray];
        elements.forEach((element, elementIndex) => {
          const index = tempWidgets.findIndex(
            (widget) => widget.id === element.id
          );
          if (index !== -1) {
            tempWidgets[index].position = elementIndex;
          }
        });
        updateWidgetArray(tempWidgets);
      });
    }
  }, [widgetArray, updateWidgetArray]);

  const [billingModelSize, setBillingModelSize] = useState<'small' | 'large'>(
    'large'
  );
  const [endOfTermSize, setEndOfTermSize] = useState<'small' | 'large'>(
    'large'
  );

  const renderPrimaryWidgets = useMemo(() => {
    return (
      <>
        {widgetArray?.length &&
          widgetArray
            .filter(
              (widget: T.Widget) =>
                widget.show ||
                // widget.id === 'deviceByGeoRegion' ||
                widget.id === 'roomsByModels' ||
                widget.id === 'deviceFamilies'
            )
            .sort((a: T.Widget, b: T.Widget) => a.position - b.position)
            .map((widget: T.Widget) => {
              const Component = componentsObject[widget.id]?.component;

              // Skip rendering if component doesn't exist
              if (!Component) {
                console.warn(`Component not found for widget: ${widget.id}`);
                return null;
              }

              const dynamicClassName = (() => {
                if (widget.id === 'billingModel') {
                  return billingModelSize === 'small'
                    ? 'rectangleCardSmall'
                    : 'squareCardLarge';
                }
                if (widget.id === 'endOfTerm') {
                  return endOfTermSize === 'small'
                    ? 'rectangleCardSmall'
                    : 'squareCardLarge';
                }
                return componentsObject[widget.id]?.className;
              })();
              return (
                <div
                  className={dynamicClassName}
                  key={widget.id}
                  id={widget.id}
                  data-testid={widget.id}
                >
                  <Suspense
                    fallback={<div style={{ minHeight: '250px' }}></div>}
                  >
                    <Component
                      {...{
                        ...componentsObject[widget.id]?.props,
                        isWideResolution,
                        navigation,
                        onSizeCategoryChange:
                          widget.id === 'billingModel'
                            ? (size: 'small' | 'large') =>
                                setBillingModelSize(size)
                            : widget.id === 'endOfTerm'
                              ? (size: 'small' | 'large') =>
                                  setEndOfTermSize(size)
                              : undefined
                      }}
                    />
                  </Suspense>
                </div>
              );
            })}
      </>
    );
  }, [
    widgetArray,
    isWideResolution,
    componentsObject,
    navigation,
    billingModelSize,
    endOfTermSize
  ]);

  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const [breadcrumbWidth, setBreadcrumbWidth] = useState(window.innerWidth);

  useEffect(() => {
    const el = breadcrumbRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBreadcrumbWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [breadcrumbLoder]);

  const navigationHandler = (url: string) => {
    navigation.push(url);
  };

  const breadcrumbNames = {
    country: t('dashboard.breadcrumb.countries', 'Countries'),
    region: t('layout.dropdown.statesOrRegions', 'States/Regions'),
    device: t('dashboard.breadcrumb.devices', 'Devices'),
    site: t('dashboard.breadcrumb.sites', 'Sites'),
    supplyroutingId: t(
      'dashboard.breadcrumb.supplyRouting',
      'Supply Routing IDs'
    ),
    billingModel: t('dashboard.breadcrumb.billingModels', 'Billing Models'),
    opportunity: t('dashboard.breadcrumb.contractId', 'Contract IDs'),
    globalOpportunity: t(
      'layout.dropdown.globalContractIds',
      'Global Contract IDs'
    )
  };

  // New type names for the breadcrumb URLs
  const breadcrumbTypes = {
    country: 'country',
    region: 'stateRegion',
    device: 'devices',
    site: 'sites',
    supplyroutingId: 'supplyRoutingIDs',
    billingModel: 'billingModels',
    opportunity: 'contractsIDs',
    globalOpportunity: 'globalContractIDs'
  };

  // Desired order of the breadcrumbs
  const breadcrumbOrder = [
    'country',
    'region',
    'device',
    'site',
    'supplyroutingId',
    'billingModel',
    'opportunity',
    'globalOpportunity'
  ];

  return (
    <>
      {isMCSDashboard || isMDSDashboard ? null : breadcrumbLoder ? (
        <S.BreadcrumbsLoaderContainer revealHeader={revealHeader}>
          <ProgressIndicator
            appearance="linear"
            behavior="indeterminate"
          />{' '}
        </S.BreadcrumbsLoaderContainer>
      ) : (
        <div ref={breadcrumbRef}>
          <S.DashBoardBreadcrumbs
            revealHeader={revealHeader}
            aria-label="Breadcrumb with dynamic levels"
            id="breadcrumb-id"
            items={breadcrumbOrder
              .map((id, index) => {
                // Find the corresponding item from the API data based on `id`
                const item = breadCrumbData.find(
                  (apiItem) => apiItem.id === id
                );
                if (!item) return null; // If no matching item found, skip this breadcrumb

                const breadcrumbText =
                  breadcrumbNames[id as keyof typeof breadcrumbNames];
                const breadcrumbLink =
                  id === 'device'
                    ? `/devices`
                    : `/breadcrumbs?type=${breadcrumbTypes[id as keyof typeof breadcrumbTypes]}`;

                return {
                  text: `${breadcrumbText}: ${item.count}`,
                  customItem: (
                    <span
                      key={index}
                      onClick={() => {
                        analytics.logEvent('Breadcrumb Clicked', {
                          event_label: breadcrumbText
                        });
                        navigationHandler(breadcrumbLink);
                      }}
                    ></span>
                  ),
                  url: breadcrumbLink
                };
              })
              .filter(Boolean)} // Filter out any null values if no matching data is found
            {...(breadcrumbWidth <= 875
              ? { responsiveBreakpoint: '875px' }
              : {})}
          />
        </div>
      )}
      <S.Container id="customerWidgets">
        <Suspense fallback={<></>}>
          <ManageWidgetModal
            widgetArray={widgetArray}
            setWidgetArray={setWidgetArray}
            isMCSDashboard={isMCSDashboard}
            isMDSDashboard={isMDSDashboard}
            revealHeader={revealHeader}
          />
        </Suspense>
        {renderPrimaryWidgets}
      </S.Container>
    </>
  );
};

export default PrimaryWidgets;
